import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import TattooDesignPage from './pages/TattooDesignPage';
import NailDesignPage from './pages/NailDesignPage';
import GalleryPage from './pages/GalleryPage';
import AboutPage from './pages/AboutPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import CookiesPage from './pages/CookiesPage';
import AdBodyFooter from './components/AdBodyFooter';
import { LanguageProvider } from './contexts/LanguageContext';

// ScrollToTop component to handle scrolling to top on page navigation
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// SEO component for updating page-specific metadata
const SEO = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    // Update page-specific metadata based on the current route
    let pageTitle = "";
    let pageDescription = "";
    
    switch (pathname) {
      case "/":
        pageTitle = "TattooNailGenius - AI-Powered Tattoo & Nail Design Generator";
        pageDescription = "Create stunning tattoo and nail art designs instantly with our AI-powered design platform. Free to use, no artistic skills required.";
        break;
      case "/tattoo-design":
        pageTitle = "Create AI Tattoo Designs | TattooNailGenius";
        pageDescription = "Generate unique, personalized tattoo designs with our AI technology. Choose styles, colors, and placement to visualize your perfect tattoo.";
        break;
      case "/nail-design":
        pageTitle = "AI Nail Art Generator | TattooNailGenius";
        pageDescription = "Create stunning nail art designs with our AI tool. Explore various styles from French tips to geometric patterns for your next manicure.";
        break;
      case "/gallery":
        pageTitle = "Design Gallery | TattooNailGenius";
        pageDescription = "Browse our collection of AI-generated tattoo and nail designs. Get inspired for your next tattoo or nail art.";
        break;
      case "/about":
        pageTitle = "About Us | TattooNailGenius";
        pageDescription = "Learn about TattooNailGenius and our mission to make custom tattoo and nail art design accessible to everyone through AI technology.";
        break;
      case "/terms":
        pageTitle = "Terms of Service | TattooNailGenius";
        pageDescription = "Read our terms of service for using the TattooNailGenius AI design platform.";
        break;
      case "/privacy":
        pageTitle = "Privacy Policy | TattooNailGenius";
        pageDescription = "Our privacy policy explains how we handle your data on the TattooNailGenius platform.";
        break;
      case "/cookies":
        pageTitle = "Cookie Policy | TattooNailGenius";
        pageDescription = "Learn about how TattooNailGenius uses cookies and local storage to enhance your experience.";
        break;
      default:
        return;
    }
    
    // Only update if we're using English (Spanish handled separately)
    if (pageTitle && pageDescription && document.documentElement.lang === 'en') {
      document.title = pageTitle;
      document.querySelector('meta[name="description"]')?.setAttribute('content', pageDescription);
      document.querySelector('meta[property="og:title"]')?.setAttribute('content', pageTitle);
      document.querySelector('meta[property="og:description"]')?.setAttribute('content', pageDescription);
      document.querySelector('meta[property="twitter:title"]')?.setAttribute('content', pageTitle);
      document.querySelector('meta[property="twitter:description"]')?.setAttribute('content', pageDescription);
    }
  }, [pathname]);
  
  return null;
};

function App() {
  return (
    <LanguageProvider>
      <Router>
        <ScrollToTop />
        <SEO />
        <div className="min-h-screen bg-gray-900 text-gray-100 flex flex-col">
          <Header />
          <main className="flex-grow">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/tattoo-design" element={<TattooDesignPage />} />
              <Route path="/nail-design" element={<NailDesignPage />} />
              <Route path="/gallery" element={<GalleryPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/cookies" element={<CookiesPage />} />
            </Routes>
          </main>
          <AdBodyFooter />
          <Footer />
        </div>
      </Router>
    </LanguageProvider>
  );
}

export default App;